import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import HomeLayout from '../components/HomeLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CreditCard, Users, Calendar, Receipt, CheckCircle, AlertTriangle, Clock, Shield, Check, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tiers = [
  { key: 'bronze', name: 'Bronze', annual: 10, perUser: 1.99, users: '1–4 users', maxUsers: 4 },
  { key: 'silver', name: 'Silver', annual: 25, perUser: 1.99, users: '5–15 users', maxUsers: 15, popular: true },
  { key: 'gold', name: 'Gold', annual: 50, perUser: 1.99, users: 'Unlimited', maxUsers: 999 },
];

const statusConfig = {
  grandfathered: { icon: Shield, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Legacy Plan' },
  trial: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', label: 'Free Trial' },
  trial_expired: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Trial Expired' },
  active: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200', label: 'Active' },
  past_due: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', label: 'Past Due' },
  suspended: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Suspended' },
};

export default function Billing() {
  const [billing, setBilling] = useState(null);
  const [subInfo, setSubInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const fetchBilling = useCallback(async () => {
    try {
      const [statusRes, txnRes, subRes] = await Promise.all([
        axios.get(`${API}/billing/status`),
        axios.get(`${API}/billing/transactions`),
        axios.get(`${API}/billing/subscription-info`).catch(() => ({ data: null })),
      ]);
      setBilling(statusRes.data);
      setTransactions(txnRes.data);
      setSubInfo(subRes.data);
    } catch {
      toast.error('Failed to load billing info');
    } finally {
      setLoading(false);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      return;
    }

    try {
      const res = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription has been updated.');
        // Clear session_id from URL
        window.history.replaceState({}, '', '/billing');
        fetchBilling();
        return;
      } else if (res.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', '/billing');
        return;
      }
      // Keep polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch {
      toast.error('Error checking payment status.');
    }
  }, [fetchBilling]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams, pollPaymentStatus]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  const handleCheckout = async (type) => {
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/billing/checkout/${type}`, { origin_url: originUrl });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel auto-renewal? Service continues until the end of your current billing period.')) {
      return;
    }
    setCancelling(true);
    try {
      await axios.post(`${API}/billing/subscription/cancel`);
      toast.success('Subscription will end at your current billing period');
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  if (user?.role !== 'management') {
    return (
      <HomeLayout title="Billing">
        <div className="text-center p-12"><p className="text-slate-600">Only management can access billing.</p></div>
      </HomeLayout>
    );
  }

  if (loading) {
    return (
      <HomeLayout title="Billing">
        <div className="flex items-center justify-center h-64 text-slate-600">Loading billing info...</div>
      </HomeLayout>
    );
  }

  const status = statusConfig[billing?.status] || statusConfig.active;
  const StatusIcon = status.icon;
  const isGrandfathered = billing?.plan_type === 'grandfathered';
  const hasDeadline = isGrandfathered && billing?.legacy_billing_deadline;
  const needsTierSelection = hasDeadline && (!billing?.plan_tier_key || billing.plan_tier_key === 'grandfathered');
  const showPaymentSection = (!isGrandfathered || hasDeadline) && !needsTierSelection;

  const handleSelectTier = async (tierKey) => {
    try {
      await axios.post(`${API}/billing/select-tier`, { tier: tierKey });
      toast.success(`${tierKey.charAt(0).toUpperCase() + tierKey.slice(1)} plan selected!`);
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to select tier');
    }
  };

  const handleRefreshFromStripe = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/billing/refresh`);
      if (res.data.changed) {
        toast.success('Billing status synced from Stripe');
      } else {
        toast.info('Already in sync with Stripe');
      }
      fetchBilling();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh from Stripe');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <HomeLayout title="Billing">
      <div data-testid="billing-page" className="space-y-6 max-w-4xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Billing & Subscription</h3>
            <p className="text-slate-600">{billing?.organization_name}</p>
          </div>
          {billing?.renewal_processing && (
            <Button
              variant="outline"
              onClick={handleRefreshFromStripe}
              disabled={refreshing}
              data-testid="refresh-billing-btn"
              className="shrink-0"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Syncing…' : 'Refresh from Stripe'}
            </Button>
          )}
        </div>

        {/* Plan Status */}
        <Card className={`p-6 border shadow-sm ${status.bg}`} data-testid="plan-status-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <StatusIcon size={32} className={status.color} />
              <div>
                <p className="text-lg font-bold text-slate-900">{status.label}</p>
                <p className="text-sm text-slate-600">{billing?.message}</p>
              </div>
            </div>
            {!isGrandfathered && billing?.pricing_tier && (
              <div className="text-right">
                <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Your Rate</p>
                <p className="text-sm text-slate-700">${billing.pricing_tier.annual_fee}/yr + ${billing.pricing_tier.per_user_monthly}/user/mo</p>
              </div>
            )}
          </div>
        </Card>

        {/* Tier Selector for Legacy Orgs */}
        {needsTierSelection && (
          <Card className="p-6 bg-white border border-slate-200 shadow-sm">
            <h4 className="text-lg font-bold text-slate-900 mb-2">Select Your Plan</h4>
            <p className="text-sm text-slate-500 mb-6">Choose a subscription tier to continue using ShopManager247 after your grace period ends.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {tiers.map((tier) => (
                <div
                  key={tier.key}
                  className={`p-5 rounded-lg border-2 transition-all ${tier.popular ? 'border-orange-400 shadow-md' : 'border-slate-200'}`}
                  data-testid={`billing-tier-${tier.key}`}
                >
                  {tier.popular && <p className="text-xs font-bold text-orange-500 uppercase mb-2">Most Popular</p>}
                  <h5 className="text-lg font-bold text-slate-900">{tier.name}</h5>
                  <p className="text-2xl font-black text-slate-900 mt-1">${tier.annual}<span className="text-sm font-normal text-slate-400">/year</span></p>
                  <p className="text-sm text-slate-500">+ ${tier.perUser}/user/month</p>
                  <p className="text-xs text-slate-400 mt-1">{tier.users}</p>
                  <Button
                    onClick={() => handleSelectTier(tier.key)}
                    className={`w-full mt-4 ${tier.popular ? 'bg-orange-500 hover:bg-orange-600' : 'bg-slate-900 hover:bg-slate-800'}`}
                    data-testid={`select-billing-tier-${tier.key}`}
                  >
                    Select {tier.name}
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        )}

        {showPaymentSection && (
          <>
            {/* Billing Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="user-count-card">
                <div className="flex items-center gap-3 mb-2">
                  <Users size={18} className="text-slate-500" />
                  <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Active Users</p>
                </div>
                <p className="text-2xl font-black text-slate-900">{billing?.user_count || 0}</p>
                <p className="text-xs text-slate-400 mt-1">${billing?.monthly_cost?.toFixed(2)}/month</p>
              </Card>

              <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="annual-status-card">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar size={18} className="text-slate-500" />
                  <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Annual Fee</p>
                </div>
                <p className="text-2xl font-black text-slate-900">${billing?.annual_fee?.toFixed(2)}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {billing?.annual_paid_until
                    ? `Paid until ${new Date(billing.annual_paid_until).toLocaleDateString()}`
                    : 'Not yet paid'}
                </p>
              </Card>

              <Card className="p-5 bg-white border border-slate-200 shadow-sm" data-testid="monthly-status-card">
                <div className="flex items-center gap-3 mb-2">
                  <CreditCard size={18} className="text-slate-500" />
                  <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Monthly Fee</p>
                </div>
                <p className="text-2xl font-black text-slate-900">${billing?.monthly_cost?.toFixed(2)}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {billing?.monthly_paid_until
                    ? `Paid until ${new Date(billing.monthly_paid_until).toLocaleDateString()}`
                    : 'Not yet paid'}
                </p>
              </Card>
            </div>

            {/* Payment Actions */}
            <Card className="p-6 bg-white border border-slate-200 shadow-sm">
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Make a Payment</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-slate-200 rounded-md">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-slate-900">Annual Registration</p>
                      <p className="text-sm text-slate-500">Yearly company registration fee</p>
                    </div>
                    <p className="text-lg font-bold text-slate-900">${billing?.annual_fee?.toFixed(2)}</p>
                  </div>
                  <Button
                    onClick={() => handleCheckout('annual')}
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    data-testid="pay-annual-btn"
                  >
                    <CreditCard size={16} className="mr-2" />
                    {billing?.annual_paid_until ? 'Renew Annual Fee' : 'Pay Annual Fee'}
                  </Button>
                </div>

                <div className="p-4 border border-slate-200 rounded-md" data-testid="monthly-subscription-card">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-slate-900">Monthly Users</p>
                      <p className="text-sm text-slate-500">{billing?.user_count} user(s) x ${billing?.pricing_tier?.per_user_monthly}/mo</p>
                      {subInfo?.has_active_subscription && (
                        <p className="text-xs text-emerald-700 mt-1 font-semibold flex items-center gap-1">
                          <CheckCircle size={12} /> Auto-renewing
                          {subInfo.subscription_status === 'canceling' && ' — cancels at period end'}
                        </p>
                      )}
                    </div>
                    <p className="text-lg font-bold text-slate-900">${billing?.monthly_cost?.toFixed(2)}</p>
                  </div>
                  {subInfo?.has_active_subscription ? (
                    <Button
                      onClick={handleCancelSubscription}
                      disabled={cancelling || subInfo.subscription_status === 'canceling'}
                      variant="outline"
                      className="w-full border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="cancel-subscription-btn"
                    >
                      {subInfo.subscription_status === 'canceling'
                        ? 'Cancellation scheduled'
                        : (cancelling ? 'Cancelling…' : 'Cancel Auto-Renew')}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleCheckout('monthly')}
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      data-testid="pay-monthly-btn"
                    >
                      <CreditCard size={16} className="mr-2" />
                      Start Monthly Auto-Pay
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </>
        )}

        {/* Payment History */}
        <Card className="p-6 bg-white border border-slate-200 shadow-sm">
          <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4 flex items-center gap-2">
            <Receipt size={16} />
            Payment History
          </h4>
          {transactions.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">No payment history</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="transactions-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {transactions.map((txn) => (
                    <tr key={txn.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 capitalize font-medium">
                        {txn.payment_type === 'annual' ? 'Annual Registration' : `Monthly Users (${txn.user_count || '—'})`}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                        ${txn.amount?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-semibold ${
                          txn.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                          txn.payment_status === 'expired' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {txn.payment_status === 'paid' ? 'Paid' : txn.payment_status === 'expired' ? 'Expired' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {isGrandfathered && !hasDeadline && (
          <p className="text-xs text-slate-400 text-center">
            Your organization was registered before billing was introduced. You have full access at no charge.
          </p>
        )}
      </div>
    </HomeLayout>
  );
}
